import { Box, Link, Stack, Typography } from '@mui/material';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';

const LOGIN_COPY = {
  badge: 'Now in beta: Piper, your AI closing partner',
  headline: "The easiest way you'll ever close a loan.",
  body: 'Originate, price, lock, and close with United Fidelity Funding Corp. — while Piper handles the busywork, watches your rates, and tells you exactly what needs you next.',
  features: [
    {
      icon: <ViewQuiltOutlinedIcon />,
      title: 'One-screen pricing & instant locks',
      desc: "Hundreds of products ranked for you — Piper's Pick surfaces the best rate and price on top.",
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Rate watch that never sleeps',
      desc: 'Piper re-prices floating loans every morning and alerts you the moment you hit your target.',
    },
    {
      icon: <CheckRoundedIcon />,
      title: 'Files that fill themselves',
      desc: 'Loan data, fees, and conditions come pre-filled and checked — you just confirm what needs you.',
    },
  ],
};

const SIGNUP_COPY = {
  badge: 'Now in beta: Piper, your AI closing partner',
  headline: 'Grow your pipeline with United Fidelity Funding Corp.',
  body: 'Join PRO Portal to price, lock, and close faster — with competitive products, a clean workflow, and Piper handling the busywork so you can originate more.',
  features: [
    {
      icon: <BoltOutlinedIcon />,
      title: 'Price and lock in minutes',
      desc: 'Run scenarios, compare products, and lock rates without leaving the file — built for brokers who move fast.',
    },
    {
      icon: <HandshakeOutlinedIcon />,
      title: 'A real wholesale partnership',
      desc: 'Work with United Fidelity Funding Corp. on a modern portal — transparent status, dedicated support, and tools that scale with you.',
    },
    {
      icon: <RocketLaunchOutlinedIcon />,
      title: 'Piper is live in beta',
      desc: "AI rate watch, Piper's Pick, and smart next steps are available now for partners.",
    },
  ],
};

/**
 * Shared left brand panel for login / signup (and other public auth shells).
 * @param {'login' | 'signup'} [variant='login']
 */
const AuthBrandPanel = ({ variant = 'login' }) => {
  const copy = variant === 'signup' ? SIGNUP_COPY : LOGIN_COPY;

  return (
    <Box
      sx={{
        display: { xs: 'none', lg: 'flex' },
        width: '48%',
        minWidth: 520,
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        color: '#fff',
        px: '56px',
        py: '48px',
        background: 'linear-gradient(160deg,#1c2428 0%,#22303a 55%,#2b2020 100%)',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -120,
          right: -120,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(225,4,4,.28),transparent 68%)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            alignSelf: 'flex-start',
            bgcolor: '#fff',
            borderRadius: '10px',
            px: '16px',
            py: '10px',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src="/assets/pp-logo.png"
            alt="PRO Portal"
            sx={{ height: 36, display: 'block' }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            maxWidth: 460,
            py: 4,
          }}
        >
          <Box
            sx={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.25,
              bgcolor: 'rgba(225,4,4,.14)',
              border: '1px solid rgba(225,4,4,.35)',
              borderRadius: '30px',
              px: '14px',
              py: '7px',
              mb: 2.5,
            }}
          >
            <Box
              component="img"
              src="/assets/piper-mark.png"
              alt=""
              aria-hidden
              sx={{
                width: 22,
                height: 22,
                objectFit: 'contain',
                bgcolor: '#fff',
                borderRadius: '6px',
                p: '2px',
                boxSizing: 'border-box',
              }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              {copy.badge}
            </Typography>
          </Box>

          <Typography
            component="h1"
            sx={{
              fontSize: 38,
              fontWeight: 800,
              letterSpacing: '-.02em',
              lineHeight: 1.12,
              mb: 2,
            }}
          >
            {copy.headline}
          </Typography>

          <Typography
            sx={{
              fontSize: 15,
              color: 'rgba(255,255,255,.72)',
              lineHeight: 1.55,
              mb: 3.5,
            }}
          >
            {copy.body}
          </Typography>

          <Stack spacing="18px">
            {copy.features.map(({ icon, title, desc }) => (
              <Stack key={title} direction="row" spacing={1.75} alignItems="flex-start">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    bgcolor: 'rgba(255,255,255,.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: '#fff',
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                >
                  {icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#fff', mb: 0.25 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,.6)', lineHeight: 1.45 }}>
                    {desc}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            borderTop: '1px solid rgba(255,255,255,.1)',
            pt: '18px',
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontStyle: 'italic',
              color: 'rgba(255,255,255,.5)',
              lineHeight: 1.4,
            }}
          >
            Powering Real Originators with the tools they need to grow.
          </Typography>
          <Box
            component={Link}
            href="https://uff.pro"
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              flexShrink: 0,
              bgcolor: '#fff',
              borderRadius: '8px',
              px: 1.25,
              py: 0.75,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src="/assets/UFF_Logo_Main_2026.png"
              alt="United Fidelity Funding Corp."
              sx={{ height: 22, display: 'block' }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthBrandPanel;
